import "./core/styles/fonts.ts";
import "./index.css";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import i18next from "i18next";
import { initI18n } from "./core/libs/i18n";
import { AppProvider } from "./core/providers";
// The entry owns the router and injects it into the core shell — core must not reach out to
// the route tree (which imports @modules/*). See AppProvider's `router` prop.
import { router } from "./router";

// Language → `<html lang>`. Applied on init AND on every change.
//
// LTR-only platform: both interface languages are left-to-right, so there is no `dir` to manage —
// `dir="ltr"` is declared once on the document in index.html. This keeps `lang` correct, which is
// what screen readers and font selection read.
const SUPPORTED_HTML_LANGS = new Set(["en", "fr"]);

function updateHtmlAttributes(lng: string): void {
  document.documentElement.lang = SUPPORTED_HTML_LANGS.has(lng) ? lng : "fr";
}

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element not found");
}

// Keep <html> in sync on every language change.
i18next.on("languageChanged", updateHtmlAttributes);

// Gate the first render on i18n being ready, so the first paint never shows raw keys.
initI18n()
  .then(() => {
    updateHtmlAttributes(i18next.resolvedLanguage ?? "en");
    createRoot(rootElement).render(
      <StrictMode>
        <AppProvider router={router} />
      </StrictMode>
    );
  })
  .catch((error: unknown) => {
    // i18n is foundational — if it can't initialize, surface it loudly at the boot boundary.
    console.error("i18n failed to initialize", error);
  });
